import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { h2, h4, paragraphMedium, paragraphSmall } from '@helpers/typography';
import CtaLinkStyles from '@stories/Components/Buttons/CtaLink/CtaLink.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { m } from 'framer-motion';
import { transparentize } from 'polished';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  // Theme //
  --bg: ${brand.white};
  overflow: hidden;

  &[data-theme='dark'] {
    --bg: ${brand.primary.blueGrey};
  }

  background-color: var(--bg);
`;
const TopContent = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: ${fluid(10, 40, Device.TabletLarge)};
  margin-bottom: ${fluid(32, 64, Device.TabletLarge)};

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
  }

  ${CtaLinkStyles.Container} {
    transform: translateY(4px);
    font-size: 20px;
  }
`;
const Title = styled.div `
  @media ${until(Device.TabletLarge)} {
    ${h2}
    font-weight: 600;
  }
  @media ${from(Device.TabletLarge)} {
    ${h4};
  }
  margin: 0 !important;
`;
const CarouselWrapper = styled.div `
  --visibleItems: 1;

  @media ${from(Device.TabletLarge)} {
    --visibleItems: 2;
  }
`;
const Item = styled.div `
  flex: 0 0 calc(100% / var(--visibleItems));
  padding-right: 16px;
  width: 100%;
`;
const CardWrapper = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-in-view'] ? 'visible' : 'hidden',
    variants: {
        hidden: {
            y: 25,
            opacity: 0,
        },
        visible: {
            y: 0,
            opacity: 1,
        },
    },
    transition: {
        duration: 0.35,
    },
})) `
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 1px 1px 5px 1px ${transparentize(0.9, brand.black)};
  background-color: ${brand.white};

  // Assume LCCI Event by default //
  --theme: ${brand.primary.orange};

  // Unfortunately you have to hardcode these as you can't import enums in style files //
  &[data-category='lcci-partner'] {
    --theme: ${brand.secondary.lightBlue};
  }

  &[data-category='member-run'] {
    --theme: ${brand.tag.press};
  }
`;
const CardInner = styled.div `
  --leftPadding: ${fluid(16, 22, Device.TabletLarge)};
  border-left: 10px solid var(--theme);
  padding: ${fluid(17, 24, Device.TabletLarge)} ${fluid(15, 32, Device.TabletLarge)}
    ${fluid(15, 32, Device.TabletLarge)} var(--leftPadding);
`;
const CardTopContent = styled.div `
  display: flex;
  gap: 16px;

  @media ${from(Device.TabletLarge)} {
    align-items: center;
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
  }
`;
const Category = styled.div `
  background-color: var(--theme);
  color: ${brand.white};
  text-transform: uppercase;
  font-weight: 600;
  padding: ${fluid(10, 15, Device.TabletLarge)} ${fluid(16, 32, Device.TabletLarge)}
    ${fluid(10, 15, Device.TabletLarge)} 22px;
  margin-left: calc(-1 * var(--leftPadding) - 1px);
  border-radius: 0px 10px 10px 0px;
  width: max-content;
  max-width: 100%;
  font-size: ${fluid(12, 18, Device.TabletLarge)};
`;
const ControlsWrapper = styled.div `
  margin-top: 32px;
`;
const GreyPill = styled.div `
  font-size: ${fluid(12, 14, Device.TabletLarge)};
  background-color: ${brand.primary.blueGrey};
  color: ${brand.black};
  text-transform: uppercase;
  border-radius: 10px;
  padding: 12px 16px;

  @media ${until(Device.TabletLarge)} {
    width: max-content;
    max-width: 100%;
  }
`;
const CardContent = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${fluid(10, 24, Device.TabletLarge)};
  padding-top: 25px;

  a {
    width: max-content;
    max-width: 100%;
  }
`;
const CardTitle = styled.div `
  ${paragraphMedium}
  margin: 0;
  font-size: ${fluid(18, 24, Device.Mobile)};
  margin-bottom: ${fluid(16, 24, Device.Mobile)};

  @media ${until(Device.TabletLarge)} {
    font-weight: 600;
  }
`;
const CardMainContentWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: ${fluid(20, 40, Device.TabletLarge)};

  span {
    opacity: 0.5;
  }
`;
const CardMainContent = styled.div `
  ${paragraphSmall}
  margin: 0;
  font-size: ${fluid(14, 16, Device.TabletLarge)};
  line-height: ${fluid(16, 20, Device.TabletLarge)};
`;
export const PastEventsCarouselStyles = {
    Container,
    TopContent,
    Title,
    CarouselWrapper,
    Item,
    CardWrapper,
    ControlsWrapper,
    CardInner,
    Category,
    CardTopContent,
    GreyPill,
    CardContent,
    CardTitle,
    CardMainContentWrapper,
    CardMainContent,
};
